import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type ReactNode,
} from "react";
import {
  isTrackReference,
  useIsSpeaking,
  useLocalParticipant,
  useMaybeRoomContext,
  useParticipants,
  useSpeakingParticipants,
  useTracks,
  VideoTrack as VideoTrackPrimitive,
  type TrackReference,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import {
  ChevronLeft,
  ChevronRight,
  GripVertical,
  Maximize2,
  Minimize2,
  Pin,
  PinOff,
} from "lucide-react";
import { isParticipantWaiting, isRecorderParticipant, parseParticipantRole } from "./config";
import { useLiveKitSession } from "./LiveKitSessionContext";
import {
  LocalScreenSharePreview,
  notifyBeforeStageFullscreen,
  STAGE_FULLSCREEN_EVENT,
} from "./LocalScreenSharePreview";
import { localCameraVideoStyle } from "./cameraVideoStyle";
import { promoteOffPageSpeakers } from "./speakingPageOrder";
import { useLocalCameraTileOrder } from "./useLocalCameraTileOrder";

type StagePin =
  | { kind: "camera"; identity: string }
  | { kind: "screen"; identity: string };

const pinBtnStyle = (active: boolean): CSSProperties => ({
  position: "absolute",
  top: 10,
  // Leave room for StageShell fullscreen control (top-right).
  right: 52,
  zIndex: 3,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 34,
  height: 34,
  borderRadius: 8,
  border: active
    ? "1px solid rgba(251,191,36,0.7)"
    : "1px solid rgba(255,255,255,0.18)",
  background: active ? "rgba(180,83,9,0.85)" : "rgba(15,23,42,0.78)",
  color: active ? "#fde68a" : "#e2e8f0",
  cursor: "pointer",
  padding: 0,
});
// CRA + TS 4.9 reject LiveKit's ReactNode return type for JSX components.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VideoTrack = VideoTrackPrimitive as any;
/** Meet-style pages: up to 9 student tiles per page. */
const TRAINER_STUDENT_PAGE_SIZE = 9;
type LiveKitMainStageProps = {
  isTrainer?: boolean;
  connectingLabel: string;
  emptyTitle: string;
  emptySubtitle: string;
  /** Identities currently shown on the middle stage (hide them from the right list). */
  onStageIdentitiesChange?: (identities: string[]) => void;
};
function findTrack(
  tracks: ReturnType<typeof useTracks>,
  predicate: (t: TrackReference) => boolean,
): TrackReference | undefined {
  for (const candidate of tracks) {
    if (isTrackReference(candidate) && predicate(candidate)) {
      return candidate;
    }
  }
  return undefined;
}
function gridColumns(count: number): number {
  if (count <= 1) return 1;
  if (count <= 4) return 2;
  if (count <= 9) return 3;
  return 4;
}
function CameraCell({
  trackRef,
  isLocal,
  canDrag,
  isDragging,
  isDropTarget,
  isPinned,
  onTogglePin,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  trackRef: TrackReference;
  isLocal: boolean;
  canDrag: boolean;
  isDragging: boolean;
  isDropTarget: boolean;
  isPinned: boolean;
  onTogglePin: () => void;
  onDragStart: (e: DragEvent) => void;
  onDragOver: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
  onDragEnd: () => void;
}) {
  const isSpeaking = useIsSpeaking(trackRef.participant);
  const role = parseParticipantRole(
    trackRef.participant.metadata,
    trackRef.participant.identity,
  );
  const name =
    (isLocal ? "You" : null) ||
    trackRef.participant.name ||
    trackRef.participant.identity;
  const subtitle =
    role === "trainer"
      ? "Trainer"
      : role === "student"
        ? "Student"
        : "Participant";
  const hasLiveVideo =
    isTrackReference(trackRef) &&
    Boolean(trackRef.publication?.track) &&
    !trackRef.publication?.isMuted;
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <div
      draggable={canDrag}
      onDragStart={canDrag ? onDragStart : undefined}
      onDragOver={canDrag ? onDragOver : undefined}
      onDrop={canDrag ? onDrop : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      title={canDrag ? "Drag to rearrange (your view only)" : undefined}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
        borderRadius: 12,
        background: "#0b0d12",
        overflow: "hidden",
        border: isPinned
          ? "2px solid rgba(251,191,36,0.85)"
          : isSpeaking
            ? "2px solid rgba(34,197,94,0.95)"
            : isDropTarget
              ? "2px solid rgba(56,189,248,0.85)"
              : "1px solid rgba(255,255,255,0.08)",
        boxShadow: isSpeaking
          ? "0 0 0 1px rgba(34,197,94,0.35)"
          : undefined,
        opacity: isDragging ? 0.55 : 1,
        cursor: canDrag ? "grab" : "default",
        transition: "border-color 120ms ease, opacity 120ms ease",
        userSelect: "none",
      }}
    >
      {hasLiveVideo ? (
        <VideoTrack
          trackRef={trackRef}
          style={localCameraVideoStyle(isLocal, {
            width: "100%",
            height: "100%",
            objectFit: "cover",
            background: "#0b0d12",
            pointerEvents: "none",
          })}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "radial-gradient(circle at 30% 20%, #1e293b 0%, #0b0d12 70%)",
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "#1e3a5f",
              color: "#93c5fd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              fontWeight: 700,
            }}
          >
            {initial}
          </div>
        </div>
      )}
      {canDrag ? (
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: 6,
            background: "rgba(0,0,0,0.55)",
            color: "#cbd5e1",
            pointerEvents: "none",
          }}
        >
          <GripVertical size={14} />
        </div>
      ) : null}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onTogglePin();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        title={isPinned ? "Unpin" : "Pin for your view"}
        aria-label={isPinned ? "Unpin" : "Pin for your view"}
        style={pinBtnStyle(isPinned)}
      >
        {isPinned ? <PinOff size={14} /> : <Pin size={14} />}
      </button>
      <div
        style={{
          position: "absolute",
          left: 8,
          bottom: 8,
          padding: "4px 8px",
          borderRadius: 6,
          background: "rgba(0,0,0,0.7)",
          color: "#fff",
          fontSize: 12,
          fontWeight: 600,
          pointerEvents: "none",
        }}
      >
        {name}
        <span style={{ color: "#94a3b8", fontWeight: 500 }}> · {subtitle}</span>
        {isPinned ? (
          <span style={{ color: "#fde68a", fontWeight: 600 }}> · Pinned</span>
        ) : null}
      </div>
    </div>
  );
}
const StageFullscreenContext = createContext(false);

function StageShell({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const sync = () => {
      setIsFullscreen(document.fullscreenElement === rootRef.current);
    };
    sync();
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  // Keep in the click stack (no setTimeout) or requestFullscreen is flaky.
  const toggleFullscreen = useCallback(() => {
    const el = rootRef.current;
    if (!el || typeof el.requestFullscreen !== "function") return;

    notifyBeforeStageFullscreen();

    const active = document.fullscreenElement;
    if (active === el) {
      void document.exitFullscreen().catch(() => undefined);
      return;
    }

    const enter = () => {
      const req = el.requestFullscreen({
        navigationUI: "hide",
      } as FullscreenOptions);
      void Promise.resolve(req).catch(() => undefined);
    };

    if (active) {
      void document
        .exitFullscreen()
        .then(enter)
        .catch(() => undefined);
      return;
    }
    enter();
  }, []);

  return (
    <StageFullscreenContext.Provider value={isFullscreen}>
      <div
        ref={rootRef}
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          minHeight: 0,
          background: isFullscreen ? "#0b0d12" : "transparent",
        }}
      >
        {children}
        <button
          type="button"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 6,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 34,
            height: 34,
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.18)",
            background: "rgba(15,23,42,0.78)",
            color: "#e2e8f0",
            cursor: "pointer",
          }}
        >
          {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      </div>
    </StageFullscreenContext.Provider>
  );
}

function ScreenShareStageBody({
  screenShare,
  isLocalShare,
  shareLabel,
  videoStyle,
  isPinned,
  onTogglePin,
}: {
  screenShare: TrackReference;
  isLocalShare: boolean;
  shareLabel: string;
  videoStyle: CSSProperties;
  isPinned: boolean;
  onTogglePin: () => void;
}) {
  const stageFullscreen = useContext(StageFullscreenContext);
  // Freeze on the click *before* FS so we keep a real frame (not a blank).
  const [freezeArmed, setFreezeArmed] = useState(false);
  useEffect(() => {
    if (!isLocalShare) return;
    const arm = () => setFreezeArmed(true);
    window.addEventListener(STAGE_FULLSCREEN_EVENT, arm);
    return () => window.removeEventListener(STAGE_FULLSCREEN_EVENT, arm);
  }, [isLocalShare]);
  useEffect(() => {
    if (!stageFullscreen) setFreezeArmed(false);
  }, [stageFullscreen]);
  const freezeLocal = isLocalShare && (freezeArmed || stageFullscreen);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        background: "#0b0d12",
      }}
    >
      {isLocalShare ? (
        <LocalScreenSharePreview
          trackRef={screenShare}
          style={videoStyle}
          freeze={freezeLocal}
        />
      ) : (
        <VideoTrack trackRef={screenShare} style={videoStyle} />
      )}
      <button
        type="button"
        onClick={onTogglePin}
        title={isPinned ? "Unpin screen" : "Pin screen for your view"}
        aria-label={isPinned ? "Unpin screen" : "Pin screen for your view"}
        style={pinBtnStyle(isPinned)}
      >
        {isPinned ? <PinOff size={14} /> : <Pin size={14} />}
      </button>
      <div
        style={{
          position: "absolute",
          left: 12,
          bottom: 12,
          padding: "6px 10px",
          borderRadius: 8,
          background: "rgba(0,0,0,0.65)",
          color: "#fff",
          fontSize: 12,
          fontWeight: 600,
          zIndex: 2,
          pointerEvents: "none",
        }}
      >
        {shareLabel}
        {isPinned ? (
          <span style={{ color: "#fde68a" }}> · Pinned</span>
        ) : null}
      </div>
    </div>
  );
}
export function LiveKitMainStage({
  isTrainer = false,
  connectingLabel,
  emptyTitle,
  emptySubtitle,
  onStageIdentitiesChange,
}: LiveKitMainStageProps) {
  const liveKit = useLiveKitSession();
  const room = useMaybeRoomContext();
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const [page, setPage] = useState(0);
  const [pinned, setPinned] = useState<StagePin | null>(null);
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  const localScreenShare = useMemo(
    () =>
      findTrack(
        tracks,
        (t) =>
          t.source === Track.Source.ScreenShare &&
          t.participant.identity === localParticipant.identity &&
          !t.publication?.isMuted,
      ),
    [localParticipant.identity, tracks],
  );
  const screenShare = useMemo(() => {
    const remote = findTrack(
      tracks,
      (t) =>
        t.source === Track.Source.ScreenShare &&
        t.participant.identity !== localParticipant.identity &&
        !t.publication?.isMuted,
    );
    if (remote) return remote;
    return localScreenShare;
  }, [localParticipant.identity, localScreenShare, tracks]);
  const trainerPresenting = Boolean(isTrainer && localScreenShare);
  const cameraTracks = useMemo(() => {
    const byIdentity = new Map<string, TrackReference>();
    for (const candidate of tracks) {
      if (candidate.source !== Track.Source.Camera) continue;
      // Prefer a real publication over a placeholder for the same identity.
      if (!isTrackReference(candidate) && byIdentity.has(candidate.participant.identity)) {
        continue;
      }
      byIdentity.set(
        candidate.participant.identity,
        candidate as unknown as TrackReference,
      );
    }

    const list: TrackReference[] = [];
    const seen = new Set<string>();

    if (isTrainer && !trainerPresenting) {
      // Meet-style: every admitted participant on the stage (cam on or avatar).
      for (const p of participants) {
        const id = p.identity;
        if (!id) continue;
        if (isRecorderParticipant(id, p.metadata)) continue;
        const isLocal = id === localParticipant.identity;
        const role = parseParticipantRole(p.metadata, id);
        // Keep waiting students off the stage until Admit.
        if (!isLocal && isParticipantWaiting(p.metadata)) continue;
        // Prefer students + local trainer; skip remote trainers if any.
        if (!isLocal && role === "trainer") continue;
        if (seen.has(id)) continue;
        seen.add(id);
        const existing = byIdentity.get(id);
        const track =
          existing ||
          ({
            participant: p,
            source: Track.Source.Camera,
          } as unknown as TrackReference);
        list.push(track);
      }
      list.sort((a, b) => {
        const aLocal =
          a.participant.identity === localParticipant.identity ? 0 : 1;
        const bLocal =
          b.participant.identity === localParticipant.identity ? 0 : 1;
        if (aLocal !== bLocal) return aLocal - bLocal;
        return (a.participant.name || a.participant.identity).localeCompare(
          b.participant.name || b.participant.identity,
        );
      });
      return list;
    }

    for (const candidate of Array.from(byIdentity.values())) {
      if (!isTrackReference(candidate)) continue;
      const pub = candidate.publication;
      if (pub && pub.isMuted) continue;
      const id = candidate.participant.identity;
      if (seen.has(id)) continue;
      seen.add(id);
      const role = parseParticipantRole(
        candidate.participant.metadata,
        candidate.participant.identity,
      );
      const isLocal = id === localParticipant.identity;
      if (isRecorderParticipant(id, candidate.participant.metadata)) continue;
      if (isTrainer) {
        // Presenting: keep own cam off the stage (sidebar only).
        if (!isLocal) continue;
      } else if (role !== "trainer" && !isLocal) {
        continue;
      }
      list.push(candidate);
    }
    list.sort((a, b) => {
      const aLocal =
        a.participant.identity === localParticipant.identity ? 1 : 0;
      const bLocal =
        b.participant.identity === localParticipant.identity ? 1 : 0;
      if (aLocal !== bLocal) return aLocal - bLocal;
      const aTrainer =
        parseParticipantRole(a.participant.metadata, a.participant.identity) ===
        "trainer"
          ? 0
          : 1;
      const bTrainer =
        parseParticipantRole(b.participant.metadata, b.participant.identity) ===
        "trainer"
          ? 0
          : 1;
      return aTrainer - bTrainer;
    });
    return list;
  }, [
    isTrainer,
    localParticipant.identity,
    participants,
    tracks,
    trainerPresenting,
  ]);
  const orderStorageKey =
    room?.name && localParticipant.identity
      ? `codelive.camOrder:${room.name}:${localParticipant.identity}`
      : null;
  const {
    orderedTracks,
    canRearrange,
    draggingId,
    overId,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    resetOrder,
  } = useLocalCameraTileOrder(cameraTracks, orderStorageKey);
  const speakingParticipants = useSpeakingParticipants();
  const speakingIds = useMemo(
    () => new Set(speakingParticipants.map((p) => p.identity)),
    [speakingParticipants],
  );

  const pinnedCameraTrack = useMemo(() => {
    if (!pinned || pinned.kind !== "camera") return undefined;
    return orderedTracks.find((t) => t.participant.identity === pinned.identity);
  }, [orderedTracks, pinned]);

  // Keep pin valid for this viewer's local layout only.
  // Do not clear screen pin on brief track gaps (common under load).
  useEffect(() => {
    if (!pinned) return;
    if (pinned.kind === "camera") {
      if (!pinnedCameraTrack) setPinned(null);
      return;
    }
    if (
      screenShare &&
      screenShare.participant.identity !== pinned.identity
    ) {
      setPinned(null);
    }
  }, [pinned, pinnedCameraTrack, screenShare]);

  // Auto-pin incoming screen share so it stays featured until unpinned.
  useEffect(() => {
    if (!screenShare) return;
    setPinned({
      kind: "screen",
      identity: screenShare.participant.identity,
    });
  }, [
    Boolean(screenShare),
    screenShare?.participant.identity,
    screenShare?.publication?.trackSid,
  ]);

  // Screen stays featured while pinned. Unpin (null) → camera gallery even if share continues.
  // Pinning a camera overrides screen for this viewer's layout only.
  const preferScreen =
    Boolean(screenShare) &&
    isTrackReference(screenShare) &&
    pinned?.kind === "screen";

  const preferPinnedCamera = Boolean(
    pinned?.kind === "camera" && pinnedCameraTrack,
  );

  const usePagination =
    isTrainer &&
    !trainerPresenting &&
    !preferScreen &&
    !preferPinnedCamera;
  const spotlightTracks = useMemo(() => {
    if (!usePagination) return orderedTracks;
    return promoteOffPageSpeakers(
      orderedTracks,
      speakingIds,
      TRAINER_STUDENT_PAGE_SIZE,
    );
  }, [orderedTracks, speakingIds, usePagination]);
  const pageCount = usePagination
    ? Math.max(1, Math.ceil(spotlightTracks.length / TRAINER_STUDENT_PAGE_SIZE))
    : 1;
  useEffect(() => {
    setPage((prev) => Math.min(prev, Math.max(0, pageCount - 1)));
  }, [pageCount]);
  const visibleCameraTracks = useMemo(() => {
    if (!usePagination) return spotlightTracks;
    const start = page * TRAINER_STUDENT_PAGE_SIZE;
    return spotlightTracks.slice(start, start + TRAINER_STUDENT_PAGE_SIZE);
  }, [spotlightTracks, page, usePagination]);

  // Right rail should not duplicate people already visible on the middle stage.
  const stageIdentities = useMemo(() => {
    if (preferScreen) return [] as string[];
    if (preferPinnedCamera && pinnedCameraTrack) {
      const pinnedId = pinnedCameraTrack.participant.identity;
      const ids = [pinnedId];
      for (const t of orderedTracks) {
        const id = t.participant.identity;
        if (id && id !== pinnedId) ids.push(id);
      }
      return ids;
    }
    if (orderedTracks.length === 0) return [] as string[];
    return visibleCameraTracks
      .map((t) => t.participant.identity)
      .filter(Boolean);
  }, [
    preferScreen,
    preferPinnedCamera,
    pinnedCameraTrack,
    orderedTracks,
    visibleCameraTracks,
  ]);

  useEffect(() => {
    onStageIdentitiesChange?.(stageIdentities);
  }, [stageIdentities, onStageIdentitiesChange]);

  const togglePinCamera = useCallback((identity: string) => {
    setPinned((prev) =>
      prev?.kind === "camera" && prev.identity === identity
        ? null
        : { kind: "camera", identity },
    );
  }, []);

  const togglePinScreen = useCallback((identity: string) => {
    setPinned((prev) =>
      prev?.kind === "screen" && prev.identity === identity
        ? null
        : { kind: "screen", identity },
    );
  }, []);

  // One StageShell for all modes so fullscreen element/state survives
  // connecting → cameras → screen-share transitions (no remount flicker).
  let stageBody: ReactNode;
  if (liveKit.connecting) {
    stageBody = (
      <div
        style={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          color: "#94a3b8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {connectingLabel}
      </div>
    );
  } else if (liveKit.error) {
    stageBody = (
      <div
        style={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          color: "#fca5a5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: 24,
        }}
      >
        {liveKit.error}
      </div>
    );
  } else if (preferScreen && screenShare) {
    const isLocalShare =
      screenShare.participant.identity === localParticipant.identity;
    const shareLabel = `${
      screenShare.participant.name || screenShare.participant.identity
    } · Screen`;
    const videoStyle: CSSProperties = {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "contain",
      background: "#0b0d12",
    };

    stageBody = (
      <ScreenShareStageBody
        screenShare={screenShare}
        isLocalShare={isLocalShare}
        shareLabel={shareLabel}
        videoStyle={videoStyle}
        isPinned
        onTogglePin={() =>
          togglePinScreen(screenShare.participant.identity)
        }
      />
    );
  } else if (preferPinnedCamera && pinnedCameraTrack) {
    const strip = orderedTracks.filter(
      (t) => t.participant.identity !== pinnedCameraTrack.participant.identity,
    );
    const noopDrag = (_e: DragEvent) => undefined;
    stageBody = (
      <div
        style={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 8,
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <div style={{ flex: 1, minHeight: 0 }}>
          <CameraCell
            trackRef={pinnedCameraTrack}
            isLocal={
              pinnedCameraTrack.participant.identity ===
              localParticipant.identity
            }
            canDrag={false}
            isDragging={false}
            isDropTarget={false}
            isPinned
            onTogglePin={() =>
              togglePinCamera(pinnedCameraTrack.participant.identity)
            }
            onDragStart={noopDrag}
            onDragOver={noopDrag}
            onDrop={noopDrag}
            onDragEnd={() => undefined}
          />
        </div>
        {strip.length > 0 ? (
          <div
            style={{
              flex: "0 0 112px",
              display: "grid",
              gridTemplateColumns: `repeat(${Math.min(strip.length, 6)}, 1fr)`,
              gap: 8,
              minHeight: 0,
            }}
          >
            {strip.slice(0, 6).map((trackRef) => {
              const identity = trackRef.participant.identity;
              return (
                <CameraCell
                  key={identity}
                  trackRef={trackRef}
                  isLocal={identity === localParticipant.identity}
                  canDrag={false}
                  isDragging={false}
                  isDropTarget={false}
                  isPinned={false}
                  onTogglePin={() => togglePinCamera(identity)}
                  onDragStart={noopDrag}
                  onDragOver={noopDrag}
                  onDrop={noopDrag}
                  onDragEnd={() => undefined}
                />
              );
            })}
          </div>
        ) : null}
        {screenShare && isTrackReference(screenShare) ? (
          <button
            type="button"
            onClick={() =>
              togglePinScreen(screenShare.participant.identity)
            }
            style={{
              alignSelf: "flex-start",
              border: "1px solid rgba(255,255,255,0.18)",
              borderRadius: 8,
              background: "rgba(15,23,42,0.78)",
              color: "#e2e8f0",
              fontSize: 11,
              fontWeight: 600,
              padding: "6px 10px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Pin size={12} />
            Pin screen
          </button>
        ) : null}
      </div>
    );
  } else if (orderedTracks.length === 0) {
    stageBody =
      screenShare && isTrackReference(screenShare) ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: 24,
          }}
        >
          <div style={{ color: "#94a3b8", fontSize: 14 }}>
            Screen share is active but unpinned
          </div>
          <button
            type="button"
            onClick={() =>
              togglePinScreen(screenShare.participant.identity)
            }
            style={{
              border: "1px solid rgba(251,191,36,0.5)",
              borderRadius: 8,
              background: "rgba(180,83,9,0.35)",
              color: "#fde68a",
              fontSize: 13,
              fontWeight: 700,
              padding: "10px 14px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Pin size={14} />
            Pin screen
          </button>
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            minHeight: 0,
            textAlign: "center",
            padding: 24,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
            {emptyTitle}
          </div>
          <div style={{ color: "#94a3b8", fontSize: 14 }}>{emptySubtitle}</div>
        </div>
      );
  } else {
    const cols = gridColumns(visibleCameraTracks.length);
    stageBody = (
      <div
        style={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 8,
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {canRearrange ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              flexShrink: 0,
              paddingRight: 44,
            }}
          >
            <span style={{ color: "#64748b", fontSize: 11, fontWeight: 500 }}>
              Drag cameras to rearrange — your view only
            </span>
            <button
              type="button"
              onClick={resetOrder}
              style={{
                border: "none",
                background: "transparent",
                color: "#94a3b8",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                padding: 0,
              }}
            >
              Reset order
            </button>
          </div>
        ) : null}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridAutoRows: "minmax(0, 1fr)",
            gap: 8,
            overflow: "hidden",
          }}
        >
          {visibleCameraTracks.map((trackRef) => {
            const identity = trackRef.participant.identity;
            return (
              <CameraCell
                key={identity}
                trackRef={trackRef}
                isLocal={identity === localParticipant.identity}
                canDrag={canRearrange}
                isDragging={draggingId === identity}
                isDropTarget={overId === identity && draggingId !== identity}
                isPinned={
                  pinned?.kind === "camera" && pinned.identity === identity
                }
                onTogglePin={() => togglePinCamera(identity)}
                onDragStart={onDragStart(identity)}
                onDragOver={onDragOver(identity)}
                onDrop={onDrop(identity)}
                onDragEnd={onDragEnd}
              />
            );
          })}
        </div>
        {screenShare && isTrackReference(screenShare) && !preferScreen ? (
          <button
            type="button"
            onClick={() =>
              togglePinScreen(screenShare.participant.identity)
            }
            style={{
              alignSelf: "center",
              border: "1px solid rgba(251,191,36,0.5)",
              borderRadius: 8,
              background: "rgba(180,83,9,0.35)",
              color: "#fde68a",
              fontSize: 12,
              fontWeight: 700,
              padding: "8px 12px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              flexShrink: 0,
            }}
          >
            <Pin size={14} />
            Pin screen
          </button>
        ) : null}
        {usePagination && pageCount > 1 ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page <= 0}
              aria-label="Previous participants page"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.18)",
                background: "rgba(15,23,42,0.78)",
                color: page <= 0 ? "#64748b" : "#e2e8f0",
                cursor: page <= 0 ? "default" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ChevronLeft size={16} />
            </button>
            <span style={{ color: "#94a3b8", fontSize: 12, fontWeight: 600 }}>
              {page + 1} / {pageCount}
              <span style={{ color: "#64748b", fontWeight: 500 }}>
                {" "}
                · {orderedTracks.length} participants
              </span>
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={page >= pageCount - 1}
              aria-label="Next participants page"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.18)",
                background: "rgba(15,23,42,0.78)",
                color: page >= pageCount - 1 ? "#64748b" : "#e2e8f0",
                cursor: page >= pageCount - 1 ? "default" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return <StageShell>{stageBody}</StageShell>;
}
